import React from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { alpha } from '@mui/material';
import palette from 'src/theme/palette';
import type { PayrollSnapshot } from 'src/types/payroll.types';
import { formatMoney } from 'src/services/payroll/calculatePay';

interface PaycheckStubProps {
  snapshot: PayrollSnapshot;
  studentName?: string;
  transactionAmount?: number;
  createdAt?: string;
  compact?: boolean;
  onClose?: () => void;
}

function formatPeriodRange(key?: string): string {
  if (!key) return 'Current Pay Cycle';
  if (key.includes('_')) {
    const [start, end] = key.split('_');
    return `${formatSimpleDate(start)} → ${formatSimpleDate(end)}`;
  }
  if (key.includes('→')) {
    return key;
  }
  return key;
}

function formatSimpleDate(dateStr?: string | null): string {
  if (!dateStr) return 'N/A';
  try {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const d = new Date(year, month, day);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function formatTimestamp(dateStr?: string | null): string {
  if (!dateStr) return 'N/A';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

export function PaycheckStub({
  snapshot,
  studentName,
  transactionAmount,
  createdAt,
  compact,
  onClose,
}: PaycheckStubProps) {
  const periods = snapshot.periods || {};
  const courses = snapshot.courses || [];
  const courseMap = new Map<string, { courseName: string; progressScore?: string }>();
  courses.forEach((c) => {
    courseMap.set(String(c.periodKey), { courseName: c.courseName, progressScore: c.progressScore });
  });

  const rawKeys = Object.keys(periods).filter((k) => !k.startsWith('_'));
  const standardIndices = ['1', '2', '3', '4', '5', '6', '7', '8'];
  const allKeys = Array.from(new Set([...standardIndices, ...rawKeys])).sort((a, b) => {
    const numA = parseInt(a, 10);
    const numB = parseInt(b, 10);
    if (!isNaN(numA) && !isNaN(numB) && numA !== numB) return numA - numB;
    return a.localeCompare(b);
  });

  // If a period has split keys like '3A' and '3B', don't show the duplicate empty '3'
  const hasSplitPeriod = (pNum: string) => rawKeys.some((k) => new RegExp(`^${pNum}[A-Z]`, 'i').test(k));
  const filteredKeys = allKeys.filter((k) => {
    if (/^\d+$/.test(k) && hasSplitPeriod(k) && !periods[k]) return false;
    return true;
  });

  const periodEntries = filteredKeys.map((key) => {
    const val = (periods as any)[key] ?? 0;
    const course = courseMap.get(key);
    return {
      period: key,
      value: Number(val) || 0,
      courseName: course?.courseName,
      progressScore: course?.progressScore,
    };
  });

  const activePeriods = periodEntries.filter((p) => p.value > 0);
  const resolvedStudentName =
    studentName ||
    `${snapshot.first_name || ''} ${snapshot.last_name || ''}`.trim() ||
    'Taylor Brooks';
  const studentId = snapshot.external_student_id || 'TEST-2026-005';
  const netAmount = transactionAmount ?? snapshot.net ?? (snapshot.gross - snapshot.tax_withheld);
  const taxPercent = Math.round((snapshot.tax_rate || 0) * 100);

  const handlePrint = () => {
    if (typeof window !== 'undefined') {
      window.print();
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: compact ? 480 : 600,
        mx: 'auto',
        bgcolor: '#FFFFFF',
        borderRadius: 3,
        border: '1px solid #E2E8F0',
        overflow: 'hidden',
        boxShadow: '0 12px 36px rgba(15, 23, 42, 0.08)',
        '@media print': {
          boxShadow: 'none',
          border: 'none',
          maxWidth: '100%',
        },
      }}
    >
      {/* 1. Header with Student Detail design styling */}
      <Box
        sx={{
          px: { xs: 2.5, sm: 3 },
          pt: 2.5,
          pb: 2,
          borderBottom: '1px solid #E2E8F0',
          bgcolor: '#FFFFFF',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="row" spacing={1.75} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: alpha(palette.primary.main, 0.08),
                color: palette.primary.main,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <Icon icon="mdi:cash-check" width={24} />
            </Box>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                <Typography variant="h6" fontWeight={800} color="#0F172A">
                  Scholar Dollar Paycheck Stub
                </Typography>
                <Chip
                  label="Direct Deposited"
                  size="small"
                  sx={{
                    bgcolor: alpha(palette.primary.main, 0.08),
                    color: palette.primary.main,
                    fontWeight: 700,
                    fontSize: '0.6875rem',
                    height: 22,
                    borderRadius: 1,
                  }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Official bi-weekly academic earnings and tax statement
              </Typography>
            </Box>
          </Stack>

          {onClose && (
            <IconButton
              size="small"
              onClick={onClose}
              aria-label="Close dialog"
              sx={{
                color: '#94A3B8',
                borderRadius: 1.5,
                p: 0.5,
                '&:hover': { bgcolor: '#F1F5F9', color: '#0F172A' },
                '@media print': { display: 'none' },
              }}
            >
              <Icon icon="mdi:close" width={20} />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* 2. Body Container */}
      <Box sx={{ p: { xs: 2.25, sm: 3 } }}>
        {/* Scholar & Pay Period Identity Card */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2.5,
            borderRadius: 2.5,
            border: '1px solid #E2E8F0',
            bgcolor: '#F8FAFC',
          }}
        >
          {/* Top Row: Scholar Name & ID */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  bgcolor: alpha(palette.primary.main, 0.1),
                  color: palette.primary.main,
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 800,
                  fontSize: '0.875rem',
                }}
              >
                {resolvedStudentName.charAt(0) || 'S'}
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={800} color="#0F172A" lineHeight={1.2}>
                  {resolvedStudentName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Enrolled Scholar
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={0.75} alignItems="center">
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.35, bgcolor: '#FFFFFF', borderRadius: 1.25, border: '1px solid #E2E8F0' }}>
                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.7rem' }}>
                  Student ID:
                </Typography>
                <Typography variant="caption" sx={{ color: '#0F172A', fontWeight: 700, fontSize: '0.7rem', fontFamily: 'monospace' }}>
                  {studentId}
                </Typography>
              </Box>
            </Stack>
          </Stack>

          <Divider sx={{ my: 1.5, borderColor: '#E2E8F0' }} />

          {/* Bottom Grid: Pay Period & Timing Metadata */}
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={5}>
              <Typography variant="caption" color="#64748B" fontWeight={700} fontSize="0.65rem" letterSpacing={0.4} display="block">
                PAY PERIOD
              </Typography>
              <Typography variant="body2" fontWeight={800} color="#0F172A" sx={{ mt: 0.25 }}>
                {formatPeriodRange(snapshot.pay_period_key)}
              </Typography>
            </Grid>

            <Grid item xs={6} sm={3.5}>
              <Typography variant="caption" color="#64748B" fontWeight={700} fontSize="0.65rem" letterSpacing={0.4} display="block">
                GRADES PULLED
              </Typography>
              <Typography variant="body2" fontWeight={700} color="#334155" sx={{ mt: 0.25 }}>
                {formatSimpleDate(snapshot.date_pulled)}
              </Typography>
            </Grid>

            <Grid item xs={6} sm={3.5}>
              <Typography variant="caption" color="#64748B" fontWeight={700} fontSize="0.65rem" letterSpacing={0.4} display="block">
                POSTED TO VAULT
              </Typography>
              <Typography variant="body2" fontWeight={700} color="#334155" sx={{ mt: 0.25 }}>
                {formatTimestamp(createdAt || snapshot.date_pulled)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* 3. Academic Period Performance Pay Cards */}
        <Box sx={{ mb: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="caption" fontWeight={800} color="#64748B" letterSpacing={0.4}>
              PERIOD GRADES (% = $1.00 EACH)
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={600} fontSize="0.7rem">
              {activePeriods.length} Active Periods
            </Typography>
          </Stack>

          <Grid container spacing={1}>
            {periodEntries.map((p) => {
              const hasScore = p.value > 0;
              return (
                <Grid item xs={3} sm={1.5} key={p.period}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1.75,
                      textAlign: 'center',
                      bgcolor: hasScore ? '#FFFFFF' : '#F8FAFC',
                      border: '1px solid',
                      borderColor: hasScore ? '#CBD5E1' : '#E2E8F0',
                      transition: 'border-color 0.15s ease',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color={hasScore ? '#475569' : '#94A3B8'}
                      fontWeight={700}
                      fontSize="0.65rem"
                    >
                      P{p.period}
                    </Typography>
                    {p.courseName && (
                      <Typography
                        variant="caption"
                        color="#64748B"
                        fontWeight={600}
                        fontSize="0.6rem"
                        display="block"
                        noWrap
                        sx={{ maxWidth: '100%', px: 0.25, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={p.courseName}
                      >
                        {p.courseName}
                      </Typography>
                    )}
                    <Typography
                      variant="subtitle2"
                      fontWeight={800}
                      color={hasScore ? '#0F172A' : '#94A3B8'}
                      lineHeight={1.1}
                      sx={{ my: 0.15 }}
                    >
                      {p.value}%
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color={hasScore ? palette.primary.main : '#94A3B8'}
                      fontSize="0.675rem"
                      display="block"
                    >
                      {hasScore ? `+$${p.value}` : '—'}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* 4. Earnings & Deductions Ledger Table */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid #E2E8F0',
            bgcolor: '#FFFFFF',
            overflow: 'hidden',
            mb: 2.25,
          }}
        >
          <Table size="small">
            <TableHead sx={{ bgcolor: '#F8FAFC' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: '0.7rem', py: 1 }}>
                  PAYROLL LEDGER
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 800, color: '#475569', fontSize: '0.7rem', py: 1 }}>
                  BREAKDOWN
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 800, color: '#475569', fontSize: '0.7rem', py: 1 }}>
                  AMOUNT
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Gross Pay */}
              <TableRow>
                <TableCell sx={{ py: 1.1 }}>
                  <Typography variant="body2" fontWeight={700} color="#0F172A" fontSize="0.8125rem">
                    Gross Academic Pay
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                    Sum of percentage points across class periods
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ py: 1.1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {activePeriods.length} Classes
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ py: 1.1 }}>
                  <Typography variant="subtitle2" fontWeight={800} color="#0F172A">
                    {formatMoney(snapshot.gross)}
                  </Typography>
                </TableCell>
              </TableRow>

              {/* Tax Withheld */}
              <TableRow>
                <TableCell sx={{ py: 1.1 }}>
                  <Typography variant="body2" fontWeight={700} color="#0F172A" fontSize="0.8125rem">
                    Classroom & State Tax Withholding
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                    Simulated withholding for Tax Day & W-2 activities
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ py: 1.1 }}>
                  <Chip
                    label={`${taxPercent}% Tax`}
                    size="small"
                    sx={{
                      bgcolor: '#F1F5F9',
                      color: '#475569',
                      fontWeight: 700,
                      fontSize: '0.675rem',
                      height: 20,
                      borderRadius: 1,
                    }}
                  />
                </TableCell>
                <TableCell align="right" sx={{ py: 1.1 }}>
                  <Typography variant="subtitle2" fontWeight={800} color="#DC2626">
                    −{formatMoney(snapshot.tax_withheld)}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {/* 5. Net Deposit Banner */}
          <Box
            sx={{
              p: 2,
              bgcolor: alpha(palette.primary.main, 0.05),
              borderTop: `1px solid ${alpha(palette.primary.main, 0.15)}`,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 1.5,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1.5,
                  bgcolor: alpha(palette.primary.main, 0.12),
                  color: palette.primary.main,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon icon="mdi:bank-outline" width={22} />
              </Box>
              <Box>
                <Typography variant="caption" fontWeight={800} color={palette.primary.main} letterSpacing={0.4}>
                  NET VAULT DEPOSIT
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" fontSize="0.725rem">
                  Credited directly to scholar checking wallet
                </Typography>
              </Box>
            </Stack>

            <Typography
              variant="h5"
              fontWeight={900}
              color={palette.primary.main}
              sx={{
                letterSpacing: -0.3,
                fontFamily: 'monospace',
                fontSize: { xs: '1.5rem', sm: '1.75rem' },
              }}
            >
              {formatMoney(netAmount)}
            </Typography>
          </Box>
        </Paper>

        {/* 6. Context Notice */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2.5, fontSize: '0.725rem', lineHeight: 1.45 }}>
          Classroom tax withholdings are officially recorded for annual Tax Day simulations and W-2 filing exercises. Net earnings are deposited directly into your scholar balance.
        </Typography>

        {/* 7. Footer Action Buttons */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ '@media print': { display: 'none' } }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<Icon icon="mdi:printer-outline" width={16} />}
            onClick={handlePrint}
            sx={{
              color: '#334155',
              borderColor: '#CBD5E1',
              fontWeight: 700,
              fontSize: '0.75rem',
              borderRadius: 1.75,
              textTransform: 'none',
              px: 2,
              '&:hover': { bgcolor: '#F8FAFC', borderColor: '#94A3B8' },
            }}
          >
            Print Statement
          </Button>

          {onClose && (
            <Button
              variant="contained"
              size="small"
              onClick={onClose}
              sx={{
                bgcolor: palette.primary.main,
                color: '#FFFFFF',
                fontWeight: 700,
                fontSize: '0.75rem',
                borderRadius: 1.75,
                textTransform: 'none',
                px: 3,
                py: 0.65,
                '&:hover': { bgcolor: '#1E5631' },
              }}
            >
              Done
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
